/**
 * Binds click handlers to Plotly categorical x-axis ticks for "failed" (red) categories only.
 * Uses whole `g.xtick` groups for a reliable hit target and pointer-events.
 */

function normalizeDistTickLabel(s) {
  return String(s || "")
    .replace(/\s+/g, " ")
    .replace(/\u00a0/g, " ")
    .trim();
}

function categoryIndexFromTickLabel(rawLabel, categories) {
  const norm = (x) => normalizeDistTickLabel(x).replace(/[\u2013\u2212\u2011]/g, "-");
  const t = norm(rawLabel);
  if (!t) return -1;
  return categories.findIndex((c) => norm(c) === t);
}

export function parseXTickGroupTranslateX(gEl) {
  if (!gEl || typeof gEl.getAttribute !== "function") return NaN;
  const attr = gEl.getAttribute("transform") || "";
  const tr = attr.match(/translate\s*\(\s*([-\d.eE+]+)/i);
  if (tr) return Number(tr[1]);
  const mat = attr.match(
    /matrix\s*\(\s*([-\d.eE+]+)\s+([-\d.eE+]+)\s+([-\d.eE+]+)\s+([-\d.eE+]+)\s+([-\d.eE+]+)\s+([-\d.eE+]+)/i
  );
  if (mat) return Number(mat[5]);
  try {
    const base = gEl.transform?.baseVal;
    const item = base?.numberOfItems ? base.getItem(0) : null;
    if (item && item.type === SVGTransform.SVG_TRANSFORM_TRANSLATE) return item.matrix.e;
    if (item && item.type === SVGTransform.SVG_TRANSFORM_MATRIX) return item.matrix.e;
  } catch {
    void 0;
  }
  return NaN;
}

function categoryIndexFromTickGroup(gEl, categories) {
  const textEl = gEl?.querySelector?.("text");
  if (!textEl) return -1;
  const raw = textEl.textContent || "";
  let idx = categoryIndexFromTickLabel(raw, categories);
  if (idx >= 0) return idx;
  const du =
    (typeof textEl.getAttribute === "function" && textEl.getAttribute("data-unformatted")) ||
    (typeof gEl.getAttribute === "function" && gEl.getAttribute("data-unformatted"));
  if (du) {
    idx = categoryIndexFromTickLabel(du, categories);
    if (idx >= 0) return idx;
  }
  return -1;
}

function collectXTickGroups(svg) {
  const cartesian = svg.querySelector(".cartesianlayer") || svg;
  const seen = new Set();
  const add = (nodeList) => {
    for (const g of nodeList || []) {
      if (g?.classList?.contains?.("xtick")) seen.add(g);
    }
  };
  add(cartesian.querySelectorAll?.(".xaxislayer-above g.xtick"));
  add(cartesian.querySelectorAll?.(".xaxislayer-below g.xtick"));
  add(cartesian.querySelectorAll?.(".subplot .xaxislayer-above g.xtick"));
  add(cartesian.querySelectorAll?.(".subplot .xaxislayer-below g.xtick"));
  if (seen.size === 0) {
    add(cartesian.querySelectorAll?.("g.xtick"));
  }
  return [...seen];
}

function indexTickGroupsToCategories(svg, groupsWithX, categories) {
  const n = categories.length;
  const indexByGroup = new Map();

  if (groupsWithX.length === n) {
    for (let i = 0; i < n; i++) indexByGroup.set(groupsWithX[i].g, i);
    return indexByGroup;
  }

  if (groupsWithX.length > n) {
    for (let i = 0; i < n; i++) indexByGroup.set(groupsWithX[i].g, i);
    return indexByGroup;
  }

  for (const { g } of groupsWithX) {
    const idx = categoryIndexFromTickGroup(g, categories);
    if (idx >= 0 && !indexByGroup.has(g)) indexByGroup.set(g, idx);
  }
  if (indexByGroup.size === n) return indexByGroup;

  const tickTexts = [];
  for (const { g } of groupsWithX) {
    const te = g.querySelector("text");
    if (te) tickTexts.push(te);
  }
  if (tickTexts.length === n) {
    const withScreenCx = tickTexts.map((textEl) => {
      const r = textEl.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      return { textEl, cx };
    });
    withScreenCx.sort((a, b) => a.cx - b.cx);
    indexByGroup.clear();
    for (let i = 0; i < n; i++) {
      const g = withScreenCx[i].textEl.closest?.("g.xtick");
      if (g) indexByGroup.set(g, i);
    }
    return indexByGroup;
  }

  const rootSvg = svg || groupsWithX[0]?.g?.ownerSVGElement;
  const tickTextsWide = [...new Set([...svgQueryAllTickTexts(rootSvg)])];
  if (tickTextsWide.length === n) {
    const withScreenCx = tickTextsWide.map((textEl) => {
      const r = textEl.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      return { textEl, cx };
    });
    withScreenCx.sort((a, b) => a.cx - b.cx);
    indexByGroup.clear();
    for (let i = 0; i < n; i++) {
      const g = withScreenCx[i].textEl.closest?.("g.xtick");
      if (g) indexByGroup.set(g, i);
    }
  }

  return indexByGroup;
}

function svgQueryAllTickTexts(svg) {
  if (!svg?.querySelectorAll) return [];
  return svg.querySelectorAll(
    ".xaxislayer-above g.xtick text, .xaxislayer-below g.xtick text, .cartesianlayer g.xtick text, g.xtick text"
  );
}

/**
 * @param {object} opts
 * @param {SVGElement|null} opts.svg
 * @param {string[]} opts.categories
 * @param {boolean[]} opts.flags - per-category red / failed flags
 * @param {(idx: number, event: MouseEvent) => void} opts.onTickClick
 * @param {boolean} [opts.enableClicks=true]
 * @returns {{ el: Element, fn: (e: Event) => void }[]}
 */
export function createRedDistributionAxisTickBindings({
  svg,
  categories,
  flags,
  onTickClick,
  enableClicks = true,
}) {
  const bindings = [];
  if (!svg || !Array.isArray(categories) || !Array.isArray(flags)) return bindings;

  const cats = categories.map((c) => String(c ?? ""));
  const n = cats.length;
  if (n === 0) return bindings;

  const tickGroups = collectXTickGroups(svg);
  const groupsWithX = tickGroups
    .map((g) => ({ g, x: parseXTickGroupTranslateX(g) }))
    .filter((o) => Number.isFinite(o.x));
  groupsWithX.sort((a, b) => a.x - b.x);

  let indexByGroup = indexTickGroupsToCategories(svg, groupsWithX, cats);

  if (indexByGroup.size < n && groupsWithX.length === n) {
    indexByGroup = new Map();
    for (let i = 0; i < n; i++) indexByGroup.set(groupsWithX[i].g, i);
  }

  for (const [gEl, idx] of indexByGroup) {
    if (idx == null || idx < 0 || idx >= n) continue;
    const textEl = gEl.querySelector?.("text");
    if (textEl) {
      if (flags[idx]) {
        textEl.style.fill = "#ef4444";
      } else {
        textEl.style.removeProperty("fill");
      }
    }
    if (!flags[idx]) continue;
    if (!enableClicks) continue;
    gEl.style.cursor = "pointer";
    gEl.style.pointerEvents = "all";
    const fn = (e) => onTickClick(idx, e);
    gEl.addEventListener("click", fn);
    bindings.push({ el: gEl, fn });
  }

  return bindings;
}
